import type { FundDetail } from "@/data/fund-detail-types";

/**
 * "Underlying Security" panel for single-stock 2x leveraged funds.
 *
 * Matches the static `etf-acls-2x.html` block at lines ~472–503:
 * - Eyebrow "Underlying Security" + subtitle
 * - Header row: # | Issuer | Listing | Industry | Exposure
 * - One data row: 01 | {ticker} / {issuer} | {listing} | {industry} | {leverageFactor}x Daily
 * - "About the Underlying Security" block with description + HQ / listing line
 *
 * Guard: renders null when underlyingIssuer is absent.
 */
export function LeveragedUnderlyingSecurity({ fund }: { fund: FundDetail }) {
  const {
    underlyingIssuer,
    underlyingTicker,
    underlyingListing,
    underlyingIndustry,
    underlyingHeadquarters,
    aboutUnderlying,
    leverageFactor,
  } = fund;

  if (!underlyingIssuer) return null;

  const exposureLabel = `${leverageFactor ?? 2}x Daily`;

  return (
    <div data-fund-section="underlyingTicker underlyingIssuer underlyingListing underlyingIndustry underlyingHeadquarters aboutUnderlying leverageFactor underlyingEtfName">
      {/* Panel header */}
      <div className="px-10 max-md:px-4 pt-5 pb-3 border-t border-[var(--etf-border)]">
        <div className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.16em] uppercase text-[var(--etf-orange)]">
          Underlying Security
        </div>
        <div className="font-[family-name:var(--font-instrument)] text-[12px] text-[var(--etf-white3)] mt-1">
          Single-issuer common stock referenced by the Fund&apos;s swap exposure
        </div>
      </div>

      {/* Header row */}
      <div className="grid grid-cols-[32px_1fr_96px_1fr_80px] max-md:grid-cols-[28px_1fr_72px] gap-4 max-md:gap-2.5 items-center px-10 max-md:px-4 py-3 border-b border-[var(--etf-border)] opacity-50 pointer-events-none">
        <span className="font-[family-name:var(--font-instrument)] text-[9px] text-[var(--etf-white3)]">
          #
        </span>
        <span className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.1em] text-[var(--etf-white3)] uppercase">
          Issuer
        </span>
        <span className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.1em] text-[var(--etf-white3)] uppercase max-md:hidden">
          Listing
        </span>
        <span className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.1em] text-[var(--etf-white3)] uppercase max-md:hidden">
          Industry
        </span>
        <span className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.1em] text-[var(--etf-white3)] uppercase text-right">
          Exposure
        </span>
      </div>

      {/* Data row */}
      <div className="grid grid-cols-[32px_1fr_96px_1fr_80px] max-md:grid-cols-[28px_1fr_72px] gap-4 max-md:gap-2.5 items-center px-10 max-md:px-4 py-4 border-b border-[var(--etf-border2)] hover:bg-white/[0.02] transition-colors">
        <span className="font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-white2)] tabular-nums">
          01
        </span>
        <div className="min-w-0">
          <div className="font-[family-name:var(--font-instrument)] text-sm font-medium text-[var(--etf-orange)] mb-0.5">
            {underlyingTicker}
          </div>
          <div className="font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-white2)] max-md:text-[13px] max-md:truncate">
            {underlyingIssuer}
          </div>
        </div>
        <span className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white2)] max-md:hidden">
          {underlyingListing ?? "—"}
        </span>
        <span className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white2)] capitalize max-md:hidden">
          {underlyingIndustry ?? "—"}
        </span>
        <span className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white)] text-right tabular-nums">
          {exposureLabel}
        </span>
      </div>

      {/* About section */}
      {(aboutUnderlying || underlyingHeadquarters) && (
        <div className="px-10 max-md:px-4 py-[18px] border-b border-[var(--etf-border2)]">
          <div className="font-[family-name:var(--font-instrument)] text-[10px] tracking-[0.16em] uppercase text-[var(--etf-white3)] mb-2">
            About the Underlying Security
          </div>
          {aboutUnderlying && (
            <p className="font-[family-name:var(--font-instrument)] text-[12px] text-[var(--etf-white2)] leading-[1.6] m-0">
              {aboutUnderlying}
            </p>
          )}
          {(underlyingHeadquarters || underlyingListing || underlyingTicker) && (
            <p className="font-[family-name:var(--font-instrument)] text-[11px] text-[var(--etf-white3)] mt-2 mb-0 tracking-[0.02em]">
              {underlyingHeadquarters && <>Headquarters: {underlyingHeadquarters}</>}
              {underlyingHeadquarters && underlyingListing && " · "}
              {underlyingListing && underlyingTicker && (
                <>
                  Listed on {underlyingListing} as {underlyingTicker}
                </>
              )}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
